import { action, computed, observable } from 'mobx';
import { IJsonSchema } from '@estee/elc-form';
import { IOptionItem } from '@estee/elc-dropdown';
import { RouterStore } from '@estee/elc-router';
import { DeliveryOptions, FIELD_TYPE } from '../../constants/Constants';
import {
  ADDRESS_NOTIFICATIONS, NOTIFICATION_GROUP, NOTIFICATION_TYPE,
} from '../../constants/Notifications';
import { IDropdownOption } from '../../domain/entities/Config/Config';
import Address, { AddressId, IAddressAttributes } from '../../domain/entities/Address';
import { IState, IFieldDependencies } from '../../domain/entities/Config';
import ShippingRepository from '../../repositories/ShippingRepository';
import SaveShippingAddressUseCase from '../../use-cases/SaveShippingAddressUseCase';
import EditShippingAddressUseCase from '../../use-cases/EditShippingAddressUseCase';
import SelectShippingAddressUseCase from '../../use-cases/SelectShippingAddressUseCase';
import SelectNewShippingAddressUseCase from '../../use-cases/SelectNewShippingAddressUseCase';
import { ICountry } from '../../api/IApiResponses';
import { getPhoneRegions } from '../../utils/AddressFormUtils';
import SetShippingMethodUseCase from '../../use-cases/SetShippingMethodUseCase';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import { DELIVERY } from '../../routes';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import AddressViewController, { IAddressViewControllerConfig } from '../AddressViewController';
import LoadStoresService from '../../application/services/LoadStoresService';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';
import SalonRepository from '../../repositories/SalonRepository';
import SessionRepository from '../../repositories/SessionRepository';
import ShippingUIState from '../../stores/UIState/ShippingUIState';
import DeleteShippingAddressUseCase from '../../use-cases/DeleteShippingAddressUseCase';
import { RESTRICTED_ADDRESS_ERRORS } from '../../constants/Errors';
import { IShippingMethod } from '../../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';

export interface IShippingViewControllerConfig extends IAddressViewControllerConfig {
  addressRepository: ShippingRepository;
  analyticsRepository: AnalyticsRepository;
  salonRepository: SalonRepository;
  sessionRepository: SessionRepository;
  shippingUIState: ShippingUIState;
  stateRouter: RouterStore;
  loadStoresService: LoadStoresService;
}

interface IShippingViewControllerData {
  addresses: Address[];
  addressDropdownOptions: Map<string, IDropdownOption[]>;
  countryId: number;
  fieldDependencies: IFieldDependencies;
  formStore: AddressFormStore;
  cities: IDropdownOption[];
  towns: string[];
  colonies: string[];
  districts: string[];
  streets: IDropdownOption[];
  wards: IDropdownOption[];
  inEditMode: boolean;
  isFpoZipEntered: boolean;
  isNewAddressSelected: boolean;
  isSavingNew: boolean;
  isUpdating: boolean;
  isUserRegistered: boolean;
  hasSuggestions: boolean;
  multipleCityState: IOptionItem[];
  offerRemoved: string;
  regions: string[] | null;
  selectedId: string;
  selectedShippingMethodId: SHIPPING_METHODS;
  selectedShippingAddress: Address;
  shippingCountries: ICountry[];
  statesList: IState[];
  validationSchema: IJsonSchema;
  enableCondensedAddress: boolean;
  condensedFields?: string[];
  hasShippingAddress: boolean;
  userFirstName: string;
  userLastName: string;
  isLoading: boolean;
  useGenericAddressHandler: boolean;
  supportsTypeaheadInput: boolean;
  isLoaded: boolean;
  shouldUseNewAddressDesign: boolean;
  shouldShowAddressDropdown: boolean;
}

export default class ShippingViewController extends AddressViewController {
  private analyticsRepository: AnalyticsRepository;

  private salonRepository: SalonRepository;

  private sessionRepository: SessionRepository;

  private stateRouter: RouterStore;

  private shippingUIState: ShippingUIState;

  private loadStoresService: LoadStoresService;

  @observable shippingCountries: ICountry[];

  @observable regions: string[] | null;

  @observable isUserRegistered: boolean;

  @observable selectedAddress: Address;

  @observable public isFpoZipEntered = false;

  @observable previousId: string;

  @observable enableCondensedAddress: boolean;

  @observable addressRepository: ShippingRepository;

  @observable isLoaded = false;

  @observable isDeleteLoading = false;

  constructor(config: IShippingViewControllerConfig) {
    super(config);
    this.analyticsRepository = config.analyticsRepository;
    this.salonRepository = config.salonRepository;
    this.sessionRepository = config.sessionRepository;
    this.stateRouter = config.stateRouter;
    this.shippingUIState = config.shippingUIState;
    this.loadStoresService = config.loadStoresService;
  }

  @action
  async loaded(): Promise<void> {
    await super.loaded();

    this.setRegions(getPhoneRegions(this.config.regionId, false));
    this.setShippingCountries(this.config.shippingCountries);
    if (!this.shouldEnableNewSchema) {
      this.setLegacySchema();
    }

    const { transaction } = this.transactionRepository;
    this.setIsUserRegistered(transaction.user.isUserRegistered);
    this.setEnableCondensedAddress(this.config.condensedAddressEnabled);

    const { addresses } = this.addressRepository;
    const defaultAddress = addresses.find(address => address.isDefault);
    this.setPreviousId(defaultAddress ? defaultAddress.id : addresses[0]?.id);

    if (transaction.shippingAddress.isSet) {
      this.formStore.setAddress(transaction.shippingAddress);
    }

    // if a new address form is about to be rendered, we don't want to reset data on component load if already touched
    if (transaction.shippingAddress?.isNewAddress && !this.formStore.isTouched) {
      await new SelectNewShippingAddressUseCase().execute();
    }
  
    if (this.inEditMode && transaction.shippingAddress.isSet) {
      this.setEditMode(true);
      // two use cases for this edit mode in current loaded method: 
      // 1. user refreshes the page when editing address form
      // 2. user clicks edit address button from payment page and then redirect to delivery page to edit address
      // when user refresh the page in edit address mode, need to reload transaction to prepare data
      if (!this.stateRouter.mobxStateStore?.isTransitioning) {
        await this.transactionRepository.loadTransaction();
      }
      const selectedAddressId = this.transactionRepository.transaction?.shippingAddress.id;
      // set second param isBillingSameAsShipping to false as not need to update the billing address
      await this.onEditAddress(selectedAddressId, false);
    }

    await this.loadAddressOptions({ countryId: this.selectedCountryId });

    if (this.config.jsonSchemasEnabled && this.selectedCountryId) {
      await this.transactionRepository.loadShippingMethods(this.selectedCountryId);
    }

    this.adjustFormValuesBasedOnSchema();

    if (transaction.shippingAddress?.attributes?.[FIELD_TYPE.BAD_FIELDS]?.length) {
      this.formStore.runValidationSchemaSync();
      this.formStore.setShowErrors(true);
    }

    this.setColoniesFromZip();
    this.isLoaded = true;
  }

  @action
  setRegions = (regions: string[] | null): void => {
    this.regions = regions;
  };

  @action
  setShippingCountries = (countries: ICountry[]): void => {
    this.shippingCountries = countries;
  };

  @action
  setIsUserRegistered = (registered: boolean): void => {
    this.isUserRegistered = registered;
  };

  @action
  setEnableCondensedAddress = (enableCondensedAddress: boolean): void => {
    this.enableCondensedAddress = enableCondensedAddress;
  };

  setLegacySchema = (): void => {
    // TODO remove this when old schema is obsolete (RCO-4685 completed), the schema
    // will be loaded on demand at the base AddressViewController based on address type
    const countryId = this.formStore?.values?.[FIELD_TYPE.COUNTRY_ID] || this.countryId;
    const hasStates = !!this.statesList?.length || !!this.config.getStates(countryId)?.length;
    this.formStore.setAddressSchema({
      properties: this.config.shippingAddressInputSchema,
      required: [],
    }, {
      hasStates,
    });
  };

  @computed
  private get inEditMode(): boolean {
    return this.transactionRepository.transaction?.user.isUserRegistered
      && this.stateRouter.queryParams
      && Boolean(Number(this.stateRouter.queryParams.inEditMode));
  }

  @computed
  get data(): IShippingViewControllerData {
    const { transaction } = this.transactionRepository;
    return {
      addressDropdownOptions: this.addressDropdownOptions,
      addresses: this.sortedAddresses,
      cities: this.cities,
      colonies: this.colonies,
      condensedFields: this.config.condensedFields,
      countryId: this.countryId,
      districts: this.districts,
      enableCondensedAddress: this.enableCondensedAddress,
      fieldDependencies: this.fieldDependencies,
      formStore: this.addressRepository.formStore,
      hasShippingAddress: transaction?.hasShippingAddress,
      hasSuggestions: this.hasSuggestions,
      inEditMode: this.inEditMode,
      isFpoZipEntered: this.isFpoZipEntered,
      isLoaded: this.isLoaded,
      isLoading: this.isLoading,
      isNewAddressSelected: this.isNewAddressSelected,
      isSavingNew: this.addressRepository.isSavingNew,
      isUpdating: this.addressRepository.isUpdating || !this.shippingUIState.addressSelectionIsEnabled,
      isUserRegistered: this.isUserRegistered,
      multipleCityState: this.multipleCityState,
      offerRemoved: this.offerRemoved,
      regions: this.regions,
      selectedId: this.transactionRepository.transaction?.shippingAddress.id,
      selectedShippingAddress: this.transactionRepository.transaction?.shippingAddress,
      selectedShippingMethodId: transaction
        ? transaction.selectedShippingMethodId
        : SHIPPING_METHODS.NONE,
      shippingCountries: this.shippingCountries,
      shouldShowAddressDropdown: this.shouldShowAddressDropdown,
      shouldUseNewAddressDesign: this.configRepository?.config?.useNewAddressDesign,
      statesList: this.statesList,
      streets: this.streets,
      supportsTypeaheadInput: this.config.supportsTypeaheadInput,
      towns: this.towns,
      useGenericAddressHandler: !!this.config?.useGenericAddressHandler,
      userFirstName: transaction?.user.firstName,
      userLastName: transaction?.user.lastName,
      validationSchema: this.validationSchema,
      wards: this.wards,
    };
  }

  @action
  setEditMode = (editMode: boolean): void => {
    this.isFpoZipEntered = false;
    this.stateRouter.changeCurrentRoute(
      DELIVERY.ROUTE, {}, editMode ? { inEditMode: Number(editMode) } : {},
    );
    this.addressRepository.setEditingAddress(editMode);
  };

  @computed
  get isWarningApoMessage(): boolean {
    const { transaction } = this.transactionRepository;
    const address = transaction?.shippingAddress;

    return transaction.selectedShippingMethodId !== SHIPPING_METHODS.STANDARD
      && transaction.selectedShippingMethodId !== SHIPPING_METHODS.STANDARD2
      && transaction.selectedShippingMethodId !== SHIPPING_METHODS.COURIER
      && address && address.isRestrictedAddress;
  }

  @computed
  private get offerRemoved(): string {
    return this.notificationsCollection.offerRemoved;
  }

  @action
  public saveEditAddress = async (
    address: Address,
    isBillingSameAsShipping: boolean,
  ): Promise<void> => {
    this.formStore.runValidationSchemaSync();
    if (this.formStore.isValid) {
      const useCase = new SaveShippingAddressUseCase(address, isBillingSameAsShipping, undefined, true);
      try {
        await useCase.execute();
        if (!this.notificationsCollection.hasNotificationsByGroup(NOTIFICATION_GROUP.SHIPPING, NOTIFICATION_TYPE.DANGER)) {
          this.setEditMode(false);
        }
        await this.salonRepository.saveUserSalon();
      } catch (error) {
        const notificationErrors = normalizeApiErrors(error, NOTIFICATION_GROUP.SHIPPING);
        const hasRestrictedAddressNotifications = RESTRICTED_ADDRESS_ERRORS.some(key => notificationErrors.some(e => e.key === key));

        if (address.isRestrictedAddress || hasRestrictedAddressNotifications) {
          this.addressRepository.updateAddressValues(address);
          this.transactionRepository.transaction.setShippingAddress(address);
          this.setEditMode(false);
        }
      }
    } else {
      this.formStore.setShowErrors(true);
    }
  };

  @action
  public onEditAddress = async (id: AddressId, isBillingSameAsShipping: boolean): Promise<void> => {
    if (!this.shippingUIState.updateAddressSelectionState) {
      return;
    }
    try {
      this.shippingUIState.updateAddressSelectionState(false);
      if (id === this.previousId && this.transactionRepository.transaction.shippingAddress.isNewAddress) {
        await this.onSelectAddress(this.previousId);
      }

      this.analyticsRepository.editDeliveryFromPayment();

      const useCase = new EditShippingAddressUseCase(id, isBillingSameAsShipping, {
        transactionLoadOptions: { shouldAwait: true },
      });
      await useCase.execute();

      if (!this.shouldEnableNewSchema) {
        this.setLegacySchema();
      }
      await this.loadAddressOptions({ countryId: this.selectedCountryId });
      this.getOptionsForEditForm();
      this.shippingUIState.updateAddressSelectionState(true);
    } finally {
      this.shippingUIState.updateAddressSelectionState(true);
    }
  };

  @action
  private setColoniesFromZip = async (): Promise<void> => {
    const { [FIELD_TYPE.ZIPCODE]: zipcode } = this.addressRepository.formStore.values;
    const { colony } = this.addressRepository.formStore.addressSchema?.properties || {};
    if (zipcode && colony) {
      const { colonies } = await this.addressRepository
        .getMunicipalityandColoniesByZip(zipcode.toString());
      this.colonies = colonies;
    }
  };

  @action
  setPreviousId = (value: string): void => {
    this.previousId = value;
  };

  @action
  public onCancelNewAddress = async (): Promise<void> => {
    this.notificationsCollection.restoreNotifications();
    const { addresses } = this.addressRepository;
    const defaultAddress = addresses.find(address => address.isDefault);
    const defaultAddressId = defaultAddress ? defaultAddress.id : addresses[0].id;
    this.onSelectAddress(this.previousId || defaultAddressId, false, true);
    this.onCancelClearSelectOptions();
  };

  @action
  public onCancelEditAddress = async (): Promise<void> => {
    const { transaction } = this.transactionRepository;
    this.shippingUIState.updateContinueButtonState(false);
    try {
      this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.SHIPPING, NOTIFICATION_TYPE.DANGER);
      this.setEditMode(false);

      if (!transaction?.deliveryTabsDisabled &&
        transaction.selectedShippingMethodId === SHIPPING_METHODS.COURIER) {
        this.loadStoresService.loadCourierStoresByLocation(transaction.shippingAddress.id);
      }
      await this.transactionRepository.loadTransaction();
      // reload the shipping methods and set the default ship method for cross border site
      if (this.configRepository.hasCrossBorderShipping) {
        await this.transactionRepository.loadShippingMethods(transaction.shippingAddress.attributes?.country_id, {
          debounceRequest: true,
          reloadAfterSetDefaultMethod: true,
          reloadTransaction: false,
        });
      }
    } finally {
      this.shippingUIState.updateContinueButtonState(true);
    }
  };

  @action
  setApoWarningMessage = (): void => {
    this.notificationsCollection.addNotification({
      group: NOTIFICATION_GROUP.SHIPPING,
      key: ADDRESS_NOTIFICATIONS.APO_NOTIFICATION,
    });
  };

  @action
  public onSelectAddress = async (id: AddressId, isSameAsBilling?: boolean, restorePreviousNotifications?: boolean): Promise<void> => {
    const {
      transaction,
      transaction: {
        shippingAddress,
      },
    } = this.transactionRepository;
    if ((shippingAddress.id !== id)) {
      if (!restorePreviousNotifications) {
        this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.SHIPPING);
        this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.PAYMENT, NOTIFICATION_TYPE.DANGER);
        this.notificationsCollection.clearNotificationByKeys(RESTRICTED_ADDRESS_ERRORS);
      }
  
      let shouldSaveSelected = true;
      if (shippingAddress.isNewAddress && this.previousId === id) {
        shouldSaveSelected = false;
      }

      const useCase = new SelectShippingAddressUseCase(id, isSameAsBilling, shouldSaveSelected, {
        transactionLoadOptions: { shouldAwait: true },
      });
      /* istanbul ignore else */
      if (!shippingAddress.isNewAddress) {
        this.setPreviousId(shippingAddress.id);
      }
      try {
        await useCase.execute();
        await this.transactionRepository.loadShippingMethodsForCrossBorderSite(transaction.shippingAddress?.attributes?.country_id);
        /* istanbul ignore else */
        if (this.isWarningApoMessage) {
          this.setApoWarningMessage();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.setIsFpoZipEntered(false);
      }
    }
  };

  @action
  setIsFpoZipEntered = (isFpoZipEntered: boolean): void => {
    this.isFpoZipEntered = isFpoZipEntered;
  };

  @action
  public onSelectNewAddress = (callback?: (zip?: string) => Promise<void>): void => {
    const {
      transaction: {
        shippingAddress,
        selectedShippingMethodId,
        setMethods,
        deliveryOptions,
      },
    } = this.transactionRepository;

    if (!shippingAddress.isNewAddress) {
      this.notificationsCollection.temporarilyStoreNotifications();
      this.formStore.setShowErrors(false);
      this.setPreviousId(shippingAddress.id);
      this.setLegacySchema();
      if ((selectedShippingMethodId in [SHIPPING_METHODS.COURIER, SHIPPING_METHODS.STANDARD] ) && callback) {
        const userAddress = this.sessionRepository.userAddress;
        const userZip = this.sessionRepository.userLocalStorageZip;
        const zip = userAddress?.postal_code || userZip;
        if (zip) {
          this.setShippingOnZipChange(FIELD_TYPE.ZIPCODE, zip);
          this.onZipChange(FIELD_TYPE.ZIPCODE, zip);
          callback(zip);
        }
      }
      const useCase = new SelectNewShippingAddressUseCase();

      useCase.execute();
    }

    if (this.config?.useShippingCalculator) {
      // if shippingCalculator is enabled and new address is selected
      // show only the first (standard) method as an option.
      const firstShipMethod: IShippingMethod = deliveryOptions?.find(delivery => delivery.name === DeliveryOptions.HOME_DELIVERY)?.shipMethods[0];

      setMethods([firstShipMethod], DeliveryOptions.HOME_DELIVERY);
      if (firstShipMethod.id !== selectedShippingMethodId) {
        this.setShippingMethod(firstShipMethod.id);
      }
    }
  };

  @action
  public setisFpoZipEntered = (city: string | number | boolean): void => {
    if (city === 'FPO' || city === 'APO' || city === 'DPO' || city === 'USPS HEADQUARTERS') {
      this.isFpoZipEntered = true;
      const { transaction } = this.transactionRepository;
      const homeShippingOptions = transaction.deliveryOptions?.find(
        delivery => delivery.name === DeliveryOptions.HOME_DELIVERY,
      )?.shipMethods;

      const standardShipMethod = homeShippingOptions.find(
        method => method.id === SHIPPING_METHODS.STANDARD
          || method.id === SHIPPING_METHODS.STANDARD2,
      );
      if (transaction.selectedShippingMethodId !== standardShipMethod.id) {
        this.setApoWarningMessage();
        this.setShippingMethod(standardShipMethod.id);
      }
    } else {
      this.isFpoZipEntered = false;
    }
  };

  @action
  public onSelectShippingFromAddressSearch(field: keyof IAddressAttributes, keywords: string, value: string): void {
    this.onSelectFromAddressSearch(field, keywords, value);
    this.setisFpoZipEntered(this.formStore.values.city);
  }

  @computed
  private get isNewAddressSelected(): boolean {
    return this.transactionRepository.transaction?.shippingAddress.isNewAddress;
  }

  public setShippingMethod = async (shippingMethodId: SHIPPING_METHODS): Promise<void> => {
    const useCase = new SetShippingMethodUseCase(shippingMethodId);
    await useCase.execute();
  };

  @action
  public setShippingOnZipChange = async (field: keyof IAddressAttributes, value: string): Promise<void> => {
    if (this.config?.useShippingCalculator) {
      const fieldSchema = this.validationSchema.properties[field];
      const pattern = new RegExp(fieldSchema.pattern);
      const { transaction } = this.transactionRepository;
      if (pattern.test(value)) {
        // With the shipping method unification refactoring, this call is still fine, as we are getting exclusive home delivery methods only
        const { deliveryMethods, orderSummary } = await this.addressRepository.getDeliveryMethodsFromZip(value);
        if (deliveryMethods && orderSummary) {
          transaction.setMethods(deliveryMethods, DeliveryOptions.HOME_DELIVERY);
          transaction.updateOrderSummary(orderSummary);
          // if received shipmethods does not include the currently selected shipmethod,
          // set shipping method to first available option
          if (!deliveryMethods.find(method => method.id === transaction.selectedShippingMethodId)) {
            this.setShippingMethod(deliveryMethods[0].id);
          }
        }
      }
    }
  };

  // For highlighting fields with error.
  @computed
  public get errorFields(): string[] {
    return this.errorFieldsToHighlight(NOTIFICATION_GROUP.SHIPPING);
  }

  @action
  public deleteShippingAddress = async (id: AddressId): Promise<void> => {
    this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.SHIPPING);
    this.isDeleteLoading = true;
    const useCase = new DeleteShippingAddressUseCase(id, {
      transactionLoadOptions: { shouldAwait: true },
    });
    await useCase.execute();
    this.setShouldShowDeleteModal(false);
    this.isDeleteLoading = false;
  };

  @computed
  get shouldShowAddressSection(): boolean {
    return (
      this.configRepository?.config?.useNewAddressDesign
        && !this.isNewAddressSelected
    );
  }

}
